import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  skipToken,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdConnectedCompaniesTypeahead,
  getV1CompaniesCompanyIdLabels,
  GetV1CompaniesCompanyIdLabelsData,
} from '@treadinc/horizon-api-spec';
import { uniqBy } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

import {
  FilterMenuItemLoadingReason,
  FilterMenuOption,
} from '~components/Filters/FilterMenuItem';
import { extractPagination, Pagination } from '~services/pagination';

const QUERY_PAGINATION_LIMIT = 10;

export interface EquipmentFilterQuery {
  clear: () => void;
  data: FilterMenuOption[];
  fetch: (searchQuery: string) => void;
  fetchMore:
    | ((
        options?: FetchNextPageOptions | undefined,
      ) => Promise<
        InfiniteQueryObserverResult<InfiniteData<FilterMenuOption[], unknown>, Error>
      >)
    | undefined;
  loadingReason?: FilterMenuItemLoadingReason;
}

type QueryParamsState = {
  shouldFetch: boolean;
  filters: { query: string };
  pagination?: Pagination & { hasMore: boolean };
};

type SimpleQueryParams = Pick<
  NonNullable<GetV1CompaniesCompanyIdLabelsData['query']>,
  'page[after]' | 'page[limit]' | 'search[query]'
>;

function deriveSimpleQueryParams(queryParams: QueryParamsState) {
  const { filters, pagination } = queryParams;

  const query: SimpleQueryParams = { 'page[limit]': QUERY_PAGINATION_LIMIT };

  if (pagination?.after) {
    query['page[after]'] = pagination.after;
  }

  if (filters.query.trim().length) {
    query['search[query]'] = filters.query.trim();
  }

  return query;
}

/**
 * Company Shares
 */
export function useCompanySharesQuery(companyId: string): EquipmentFilterQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_companyShares';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query = deriveSimpleQueryParams({
            ...queryParams,
            pagination: pageParam,
          });

          return getV1CompaniesCompanyIdConnectedCompaniesTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const companyShares = response.data.data.map((company) => {
              return { label: company.legal_name, value: company.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return uniqBy(companyShares, (item: { value: string }) => item.value);
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Labels
 */
export function useEquipmentLabelsQuery(companyId: string): EquipmentFilterQuery {
  const queryKey = 'EquipmentFiltersQueries_labels';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query = deriveSimpleQueryParams({
            ...queryParams,
            pagination: pageParam,
          });

          // FIXME @todo TRE-12887 -> To be replaced with a proper typeahead endpoint when available
          return getV1CompaniesCompanyIdLabels({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const labels = response.data.data.map((label) => {
              return { label: label.name, value: label.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return uniqBy(labels, (item: { value: string }) => item.value);
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({
      shouldFetch: true,
      filters: { query: searchQuery },
    });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}
